'use client';

import {
  flex,
  linkOverlay,
  text,
  vstack,
} from '@onbeam/business-styled-system/patterns';
import { NewsCardProps } from './NewsCard.types';
import { format } from 'date-fns';
import Image from 'next/image';
import { css, cx } from '@onbeam/business-styled-system/css';
import { useBreakpoint } from '@onbeam/utils';
import { token } from '@onbeam/business-styled-system/tokens';
import { Link } from '@onbeam/business-ui';

export const NewsCard = ({
  title,
  date,
  description,
  image,
  href,
}: NewsCardProps) => {
  const isMobile = useBreakpoint({ max: token('breakpoints.md') });

  return (
    <div
      className={cx(
        'group',
        flex({
          position: 'relative',
          flexDirection: 'column',
          gap: '4',
          rounded: 'sm',
          w: 'full',
          md: {
            flexDirection: 'row',
            gap: '6',
            h: '[8.625rem]',
          },
        }),
      )}
    >
      <div
        className={css({
          position: 'relative',
          rounded: 'xs',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'opacity.md',
          w: 'full',
          h: '[10.125rem]',
          md: {
            w: '[20.3125rem]',
            h: 'full',
            flexShrink: 0,
          },
        })}
      >
        <Image
          src={image}
          alt=""
          width={325}
          height={138}
          className={css({
            position: 'absolute',
            inset: '0',
            h: 'full',
            w: 'full',
            objectFit: 'cover',
            transitionProperty: 'transform',
            transitionDuration: 'normal',
            transitionTimingFunction: 'ease-out',
            _groupHover: {
              transform: 'scale(1.1)',
            },
          })}
        />
      </div>
      <div className={vstack({ gap: '2', overflow: 'hidden' })}>
        <Link
          href={href}
          iconSize={isMobile ? 18 : 24}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(
            linkOverlay(),
            text({
              color: 'mono.primaryBg',
              style: 'body-xl',
              truncate: true,
              w: 'fit',
              md: { textStyle: 'subtitle-sm' },
            }),
          )}
        >
          {title}
        </Link>
        <p
          className={text({
            style: 'body-md',
            color: 'mono.tertiary',
            md: { textStyle: 'body-lg', lineClamp: 3 },
          })}
        >
          {description}
        </p>
        <span
          className={text({
            style: 'body-sm',
            color: 'mono.tertiary',
            mt: 'auto',
          })}
        >
          {format(date, 'MMM dd, yyyy')}
        </span>
      </div>
    </div>
  );
};
